import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Shield, Building2, Mail } from 'lucide-react';
import { CopyButton } from '@/components/admin/copy-button';
import { FilterChips } from '@/components/admin/filter-chips';
import { EmptyState } from '@/components/ui/empty-state';
import { useOrganizationKindLabel } from '@/lib/labels';
import { formatCount } from '@/lib/format';
import { useTranslations } from '@/i18n/use-locale';
import { useCityLabel } from '@/i18n/use-city-label';
import { ORGANIZATION_KIND, type OrganizationKind } from '@/contracts';
import { type ListReturnState } from '@/lib/detail';
import { QueryGate } from '@/components/ui/query-gate';
import { useOrganizationsQuery } from '@/lib/queries';
import { StatusBadge } from '@/components/ui/status-badge';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';

type KindFilter = 'all' | OrganizationKind;

const KINDS: readonly string[] = Object.values(ORGANIZATION_KIND);

/** The kind chip lives in `?kind=`, so the detail screen's back link can restore it. */
function parseKind(value: string | null): KindFilter {
  return value && KINDS.includes(value) ? (value as OrganizationKind) : 'all';
}

export function OrganizationsRoute() {
  const t = useTranslations('admin.organizations');
  const common = useTranslations('common');
  const cityLabel = useCityLabel();
  const kindLabel = useOrganizationKindLabel();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const kindFilter = parseKind(searchParams.get('kind'));
  const returnState: ListReturnState = { from: `${location.pathname}${location.search}` };

  const setKindFilter = (next: KindFilter) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'all') params.delete('kind');
        else params.set('kind', next);
        return params;
      },
      { replace: true },
    );

  const orgsQuery = useOrganizationsQuery(kindFilter);

  return (
    <QueryGate query={orgsQuery}>
      {(data) => {
        const orgs = data.items;

        return (
          <div className="space-y-6">
            <PageHeader title={t('screenTitle')} sub={t('screenHint')} />

            <FilterChips<KindFilter>
              label={t('kindLabel')}
              value={kindFilter}
              onChange={setKindFilter}
              options={[
                { value: 'all', label: t('kindFilter.all') },
                { value: 'club', label: t('kindFilter.club') },
                { value: 'federation', label: t('kindFilter.federation') },
                { value: 'academy', label: t('kindFilter.academy') },
                { value: 'agent', label: t('kindFilter.agent') },
                { value: 'sports_institution', label: t('kindFilter.sports_institution') },
                { value: 'other', label: t('kindFilter.other') },
              ]}
            />

            {/* Grid of Cards */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {orgs.map((club) => (
                // The whole card opens the organization, by pointer or by Enter/Space on the
                // focused card; the name stays a link, and the card's own controls (the copy
                // button) keep their clicks.
                <Card
                  key={club.id}
                  data-testid="organization-card"
                  aria-label={club.name}
                  onActivate={() =>
                    void navigate(`/organizations/${club.id}`, { state: returnState })
                  }
                  className="flex flex-col justify-between p-5 shadow-sm"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="text-[16px] font-bold text-navy">
                            <Link
                              to={`/organizations/${club.id}`}
                              state={returnState}
                              className="rounded transition hover:text-pitch hover:underline focus-visible:ring-2 focus-visible:ring-pitch focus-visible:outline-none"
                            >
                              {club.name}
                            </Link>
                          </h3>
                          {club.verificationStatus === 'verified' && (
                            <span title={t('verifiedAccount')}>
                              <Shield className="h-4 w-4 text-gold-700" />
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[12px] text-conc-500">
                          {kindLabel(club.kind)}
                          {cityLabel(club.city) && ` · ${cityLabel(club.city)}`}
                        </p>
                        {/* The owner signs in with this address: it is the account's identity,
                      and the one the team writes to about the account. */}
                        {club.officialEmail && (
                          <p className="mt-1.5 flex items-center gap-1 text-[12px] text-conc-600">
                            <Mail aria-hidden className="h-3.5 w-3.5 shrink-0 text-conc-400" />
                            <span className="sr-only">{t('officialEmail')}</span>
                            <span className="ltr-field truncate">{club.officialEmail}</span>
                            <CopyButton
                              value={club.officialEmail}
                              label={common('copyEmail')}
                              copiedLabel={common('copied')}
                            />
                          </p>
                        )}
                      </div>
                      <StatusBadge
                        tone={club.verificationStatus === 'verified' ? 'approved' : 'neutral'}
                      >
                        {club.verificationStatus === 'verified'
                          ? t('filter.verified')
                          : t('unverified')}
                      </StatusBadge>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2 rounded-xl bg-sideline p-3 text-center text-[12px]">
                      <div>
                        <p className="num font-bold text-navy">{formatCount(club.memberCount)}</p>
                        <p className="text-[11px] text-conc-500">{t('usersCount')}</p>
                      </div>
                      <div>
                        <p className="num font-bold text-navy">{formatCount(club.profileViews)}</p>
                        <p className="text-[11px] text-conc-500">{t('viewsCount')}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {orgs.length === 0 && (
              <EmptyState
                icon={Building2}
                title={t('emptyKind.title')}
                hint={t('emptyKind.hint')}
              />
            )}
          </div>
        );
      }}
    </QueryGate>
  );
}
